/**
 * CJAS — accessibility and large-text mode.  Jira CA-55.
 *
 * The congregation includes a lot of people over seventy. Large text is not
 * a compliance checkbox here, it is the difference between a member reading
 * the Fajr time and asking someone else to read it for them.
 *
 * Two rules run through this file:
 *   1. Never cap the user's text size below what the OS offers. Clamping to
 *      "1.3x max because the layout breaks" is the layout's problem.
 *   2. Nothing important may be conveyed by colour alone — the app is used by
 *      people with colour vision deficiency and on TV screens with terrible
 *      colour reproduction.
 */

const SCALE_MIN = 1.0;
const SCALE_MAX = 3.0;              // iOS AX5 is roughly 3.1x; we honour it
const TOUCH_TARGET_MIN_DP = 44;     // WCAG 2.5.5 / Apple HIG
const BODY_BASE_PX = 16;
const LINE_HEIGHT_MIN = 1.5;        // WCAG 1.4.12
const AA_NORMAL = 4.5;
const AA_LARGE = 3.0;
/**
 * WCAG 2.1 defines "large scale" text as 18pt regular OR 14pt bold. At the
 * 96dpi CSS reference those are TWO DIFFERENT pixel values, and conflating
 * them is the classic way a palette is signed off as AA and is not:
 *   18pt regular = 24px
 *   14pt bold    = 18.66px
 * Using 18.66 for regular text — which this file did until 2026-09-07 —
 * lets anything between 18.66px and 24px through at 3.0:1 when the standard
 * requires 4.5:1. On this project that is the size band the banner accent
 * labels and the prayer-row secondary text sit in.
 */
const LARGE_TEXT_PX = 24;           // 18pt regular
const LARGE_BOLD_PX = 18.66;        // 14pt bold

function clampScale(osScale) {
  const s = Number(osScale);
  if (!Number.isFinite(s)) return SCALE_MIN;
  return Math.min(SCALE_MAX, Math.max(SCALE_MIN, s));
}

/**
 * Type scale at a given user setting.
 *
 * The countdown deliberately scales LESS than body text. At 3x a countdown
 * sized for the hero slot stops fitting on the screen at all, and a number
 * that has scrolled off is less readable than a smaller one that has not.
 * Body copy, which is what people actually struggle with, gets the full ride.
 */
function typeScale(osScale = 1) {
  const s = clampScale(osScale);
  const soften = (base, factor) => Math.round(base * (1 + (s - 1) * factor) * 10) / 10;
  return {
    scale: s,
    body: Math.round(BODY_BASE_PX * s * 10) / 10,
    label: Math.round(13 * s * 10) / 10,
    heading: soften(22, 0.85),
    prayerTime: soften(28, 0.7),
    countdown: soften(44, 0.45),
    lineHeight: LINE_HEIGHT_MIN,
  };
}

/**
 * Above this point single-line rows must become stacked ones. Returning a
 * layout decision rather than a pixel value keeps the rule in one place
 * instead of scattered through twenty components.
 *
 * CHANGED 2026-09-10 (CA-55, audit A11Y-APP-04): tab labels are no longer
 * dropped at 2x. They are CAPPED instead (TAB_LABEL_MAX_SCALE) — the tab bar
 * is chrome that is on every screen, and a label that stops growing at 1.3x
 * always fits its column, where an icon-only bar leaves an elderly member
 * guessing what a pictogram means. "Keep the labels as long as they fit" was
 * the design note; with the cap they always fit.
 */
function layoutMode(osScale = 1) {
  const s = clampScale(osScale);
  const chrome = { tabLabels: true, iconOnly: false, tabLabelMaxScale: TAB_LABEL_MAX_SCALE,
                   headerMaxScale: HEADER_MAX_SCALE };
  if (s >= 2.0) return { mode: 'stacked', prayerRows: 'two_line', weekGrid: 'cards', shortcuts: 'list', headerScrolls: true, ...chrome };
  if (s >= 1.4) return { mode: 'relaxed', prayerRows: 'two_line', weekGrid: 'cards', shortcuts: 'grid', headerScrolls: true, ...chrome };
  return { mode: 'default', prayerRows: 'single_line', weekGrid: 'table', shortcuts: 'row', headerScrolls: false, ...chrome };
}

/**
 * The in-app text size (CA-55, A11Y-APP-13). Many elderly members have never
 * opened the phone's accessibility settings; "Stor tekst" in the app's own
 * Settings is one tap away. It MULTIPLIES the phone's setting — it never
 * replaces it, and it can never make text smaller than the phone asks for.
 */
const USER_TEXT_SIZES = { normal: 1.0, large: 1.3, largest: 1.6 };
/** The tab bar and the header are on every screen; they stop growing here. */
const TAB_LABEL_MAX_SCALE = 1.3;
const HEADER_MAX_SCALE = 1.5;

function userFactor(size) {
  if (typeof size === 'number' && Number.isFinite(size) && size > 0) return size;
  return USER_TEXT_SIZES[size] || 1;
}
function osFactor(osScale) {
  const s = Number(osScale);
  return Number.isFinite(s) && s > 0 ? s : 1;
}

/**
 * The one number layouts decide on: the phone's scale times the in-app
 * choice, clamped to what this file promises (1x..3x).
 */
function combinedScale(osScale = 1, userSize = 'normal') {
  return Math.round(clampScale(osFactor(osScale) * userFactor(userSize)) * 1000) / 1000;
}

/**
 * How big one text role ends up, and how much of that the APP must apply.
 *
 * React Native multiplies every fontSize by the OS font scale itself (capped
 * only by maxFontSizeMultiplier). So the app must never multiply by the OS
 * scale a second time — it supplies only the rest: `applied = total / os`.
 *
 *   role.scale  how much of the growth this role takes (body 1, countdown
 *               0.45 — see typeScale); 1 when omitted
 *   role.cap    the most this role may ever grow (the tab bar, the header)
 *
 * The in-app setting may lift text up to SCALE_MAX, never beyond; the OS on
 * its own is never capped for body text (rule 1 above) — only chrome with an
 * explicit `cap`, and the dampened hero roles, grow less than the OS asks.
 */
function textFactor(role = {}, osScale = 1, userSize = 'normal') {
  const os = osFactor(osScale);
  // Never smaller than the design (clampScale's floor), never capped below the OS.
  const full = Math.max(1, os, Math.min(os * userFactor(userSize), SCALE_MAX));
  const damp = typeof role.scale === 'number' ? role.scale : 1;
  let total = 1 + (full - 1) * damp;
  if (typeof role.cap === 'number') total = Math.min(total, Math.max(1, role.cap), full);
  const r = (n) => Math.round(n * 1000) / 1000;
  return { total: r(total), applied: r(total / os), os: r(os) };
}

/**
 * How many columns of a grid fit, given the width and the rendered text size.
 * `emPerCell` is how wide one cell must be, in ems of that text. Used by the
 * week cards and the shortcuts so a layout is chosen by what fits, not by a
 * breakpoint that was right on one phone.
 */
function gridColumns(widthPx, fontPx, emPerCell, max = 3) {
  const w = Number(widthPx), f = Number(fontPx), e = Number(emPerCell);
  if (!(w > 0) || !(f > 0) || !(e > 0)) return 1;
  return Math.max(1, Math.min(max, Math.floor(w / (f * e))));
}

/** Touch targets grow with text but never shrink below the floor. */
function touchTarget(osScale = 1) {
  return Math.max(TOUCH_TARGET_MIN_DP, Math.round(TOUCH_TARGET_MIN_DP * clampScale(osScale)));
}

const srgb = c => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };

function luminance(hex) {
  const h = String(hex).replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16), g = parseInt(full.slice(2, 4), 16), b = parseInt(full.slice(4, 6), 16);
  return 0.2126 * srgb(r) + 0.7152 * srgb(g) + 0.0722 * srgb(b);
}

function contrast(fg, bg) {
  const a = luminance(fg), b = luminance(bg);
  const ratio = (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  return Math.round(ratio * 100) / 100;
}

/**
 * Pass/fail against AA. Note the large-text threshold applies to the size the
 * text is ACTUALLY rendered at, so a scaled-up label can pass at 3x while
 * failing at 1x — which is why we always check the 1x case.
 */
function meetsAA(fg, bg, sizePx = BODY_BASE_PX, bold = false) {
  const ratio = contrast(fg, bg);
  const large = bold ? sizePx >= LARGE_BOLD_PX : sizePx >= LARGE_TEXT_PX;
  const required = large ? AA_LARGE : AA_NORMAL;
  return { ratio, required, pass: ratio >= required, large };
}

/**
 * Reduced motion. The prayer countdown is the one thing that must keep
 * updating — it is information, not decoration — but it stops animating and
 * simply re-renders the number.
 */
function motion(reduceMotion = false) {
  return reduceMotion
    ? { transitions: false, countdownTick: 'text_only', slideTransition: 'cut',
        autoRotate: true, durationMs: 0 }
    : { transitions: true, countdownTick: 'animated', slideTransition: 'fade',
        autoRotate: true, durationMs: 300 };
}

/**
 * What a screen reader announces for a prayer row. Written out because
 * "05:12 05:45" read aloud is meaningless — and because Urdu and Norwegian
 * screen readers need the label, not a visual column position.
 */
/** `closed`: the mosque is shut that day. Defaults to "no jamaat", as before; a timetable of start times only passes false. */
function announce(prayer, begins, jamaat, lang = 'no', closed = jamaat == null) {
  const L = {
    no: { begins: 'begynner', jamaat: 'jamaat', closed: 'moskeen er stengt' },
    en: { begins: 'begins', jamaat: 'congregation', closed: 'the mosque is closed' },
    ur: { begins: 'شروع', jamaat: 'جماعت', closed: 'مسجد بند ہے' },
  }[lang] || {};
  if (closed) return `${prayer}, ${begins} ${L.begins}. ${L.closed}.`;
  if (!jamaat) return `${prayer}, ${L.begins} ${begins}.`;
  return `${prayer}, ${L.begins} ${begins}, ${L.jamaat} ${jamaat}.`;
}

/**
 * The design tokens this module is allowed to name. Every one of these is
 * defined in prototype/assets/tokens.css in BOTH themes and carries text at
 * >= 4.5:1 on the surface it sits on. A status may not name a token that is
 * only a brand fill (--gold, --green), because those fail as text.
 */
const TEXT_TOKENS = ['--danger', '--ink', '--grey-text', '--gold-text', '--green-text'];

/**
 * Status must never be colour alone (WCAG 1.4.1). Every state carries an icon
 * and a text label as well — this is also what makes the TV screens legible
 * on a washed-out projector.
 */
function statusPresentation(state) {
  const map = {
    live:     { colour: '--danger',     icon: 'dot',      label_no: 'DIREKTE',  label_en: 'LIVE' },
    janazah:  { colour: '--ink',        icon: 'crescent', label_no: 'JANAZAH',  label_en: 'JANAZAH' },
    closed:   { colour: '--grey-text',  icon: 'lock',     label_no: 'STENGT',   label_en: 'CLOSED' },
    changed:  { colour: '--gold-text',  icon: 'star',     label_no: 'ENDRET',   label_en: 'CHANGED' },
    next:     { colour: '--green-text', icon: 'arrow',    label_no: 'NESTE',    label_en: 'NEXT' },
  };
  const p = map[state];
  return p ? { ...p, colourAlone: false } : null;
}

/** The checks that must pass before a build ships (feeds the QA plan, CA-81). */
function auditChecklist() {
  return [
    { id: 'text_300', rule: 'Text scales to 300% without loss of content or function', wcag: '1.4.4' },
    { id: 'contrast_aa', rule: 'All text meets 4.5:1 (3:1 for large text) at default size', wcag: '1.4.3' },
    { id: 'no_colour_only', rule: 'No state conveyed by colour alone', wcag: '1.4.1' },
    { id: 'touch_44', rule: 'Every interactive target at least 44dp', wcag: '2.5.5' },
    { id: 'labels', rule: 'Every control has an accessible name in all three languages', wcag: '4.1.2' },
    { id: 'focus_order', rule: 'Focus order follows reading order, including in RTL Urdu', wcag: '2.4.3' },
    { id: 'reduced_motion', rule: 'Honours the OS reduce-motion setting', wcag: '2.3.3' },
    { id: 'rtl_mirror', rule: 'Layout mirrors correctly in Urdu; times stay left-to-right', wcag: '1.3.2' },
    { id: 'line_height', rule: 'Body line-height at least 1.5', wcag: '1.4.12' },
    { id: 'orientation', rule: 'Works in portrait and landscape', wcag: '1.3.4' },
  ];
}

module.exports = {
  TEXT_TOKENS,
  SCALE_MIN, SCALE_MAX, TOUCH_TARGET_MIN_DP, BODY_BASE_PX, LINE_HEIGHT_MIN,
  AA_NORMAL, AA_LARGE, LARGE_TEXT_PX, LARGE_BOLD_PX,
  USER_TEXT_SIZES, TAB_LABEL_MAX_SCALE, HEADER_MAX_SCALE,
  clampScale, typeScale, layoutMode, touchTarget, combinedScale, textFactor, userFactor, gridColumns,
  luminance, contrast, meetsAA, motion, announce, statusPresentation, auditChecklist,
};
